import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Plus, Trash2, X, ShieldCheck, Pencil } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { email: "", password: "", name: "", role: "customer", customer_id: "", phone: "", hide_payout: false, driver_id: "" };

const ROLE_LABEL = {
  admin: "Administrator",
  dispatcher: "Disponent",
  customer: "Kunde",
  driver: "Fahrer",
};

const ROLE_BADGE_CLASS = {
  admin: "border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37]",
  dispatcher: "border-blue-500/40 bg-blue-500/10 text-blue-800",
  customer: "border-emerald-500/40 bg-emerald-500/10 text-emerald-800",
  driver: "border-orange-500/40 bg-orange-500/10 text-orange-800",
};

export default function Users() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState("");

  const customerMap = React.useMemo(() => {
    const m = {};
    customers.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [customers]);

  const driverMap = React.useMemo(() => {
    const m = {};
    drivers.forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [drivers]);

  const fetchAll = async () => {
    const [u, c, d] = await Promise.all([api.get("/users"), api.get("/customers"), api.get("/drivers")]);
    setRows(u.data);
    setCustomers(c.data);
    setDrivers(d.data);
  };
  useEffect(() => { fetchAll(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setError("");
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditId(row.id);
    setForm({
      email: row.email || "",
      password: "",  // leave empty unless user wants to change it
      name: row.name || "",
      role: row.role || "customer",
      customer_id: row.customer_id || "",
      phone: row.phone || "",
      hide_payout: !!row.hide_payout,
      driver_id: row.driver_id || "",
    });
    setError("");
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (payload.role !== "customer") payload.customer_id = null;
      else if (!payload.customer_id) {
        setError("Bitte einen Kunden auswählen für die Rolle 'Kunde'.");
        return;
      }
      if (!payload.phone) delete payload.phone;
      if (editId) {
        // Edit mode: only send patchable fields; don't send empty password
        const patch = {
          name: payload.name,
          role: payload.role,
          customer_id: payload.customer_id,
          phone: payload.phone,
          // hide_payout is meaningful only for drivers; for other roles force false
          hide_payout: payload.role === "driver" ? !!payload.hide_payout : false,
          driver_id: payload.role === "driver" ? (payload.driver_id || "") : "",
        };
        if (payload.password) patch.password = payload.password;
        await api.put(`/users/${editId}`, patch);
        toast.success("Benutzer aktualisiert", { description: payload.email });
      } else {
        await api.post("/users", payload);
        toast.success("Benutzer erstellt", { description: payload.email });
      }
      setOpen(false);
      setEditId(null);
      setForm(emptyForm);
      fetchAll();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error("Speichern fehlgeschlagen", { description: msg });
    }
  };

  const del = async (id, email) => {
    if (!window.confirm(`Benutzer ${email} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Benutzer gelöscht", { description: email });
      fetchAll();
    } catch (err) {
      const msg = formatApiError(err);
      toast.error("Löschen fehlgeschlagen", { description: msg });
      window.alert(msg);
    }
  };

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Zugang &amp; Sicherheit</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">Benutzer</h1>
            <p className="text-sm text-[var(--text-3)] mt-2 max-w-2xl">
              Verwalte Login-Accounts und ihre Verknüpfung zu Kunden. Ein Kundenbenutzer sieht nur die Aufträge des
              hier hinterlegten Kunden.
            </p>
          </div>
          <button
            onClick={openCreate}
            data-testid="user-add-button"
            className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
          >
            <Plus size={16} strokeWidth={1.8} /> Neuer Benutzer
          </button>
        </div>

        <div className="card mt-6 overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">E-Mail</th>
                <th className="text-left py-3 px-4">Name</th>
                <th className="text-left py-3 px-4">Rolle</th>
                <th className="text-left py-3 px-4">Verknüpfter Kunde</th>
                <th className="text-left py-3 px-4">Telefon</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-[var(--text-3)]">Keine Benutzer erfasst.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`user-row-${r.id}`}>
                  <td className="py-3 px-4 text-[var(--text)] font-mono text-xs">{r.email}</td>
                  <td className="py-3 px-4 text-[var(--text)]">{r.name || "—"}</td>
                  <td className="py-3 px-4">
                    <span
                      data-testid={`user-role-${r.id}`}
                      className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${ROLE_BADGE_CLASS[r.role] || "border-[var(--border-strong)] text-[var(--text-2)]"}`}
                    >
                      {ROLE_LABEL[r.role] || r.role}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-[var(--text)]" data-testid={`user-customer-${r.id}`}>
                    {r.role === "customer"
                      ? (r.customer_id ? (customerMap[r.customer_id] || `(unbekannt: ${r.customer_id})`) : <span className="text-red-700">⚠ nicht verknüpft</span>)
                      : r.role === "driver" && r.driver_id
                        ? <span className="text-orange-800 text-xs">Sub-Konto von {driverMap[r.driver_id] || "(unbekannt)"}</span>
                        : <span className="text-[var(--text-3)]">—</span>}
                  </td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{r.phone || "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`user-edit-${r.id}`}
                      className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"
                      title="Bearbeiten"
                    >
                      <Pencil size={16} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={() => del(r.id, r.email)}
                      data-testid={`user-delete-${r.id}`}
                      className="text-[var(--text-3)] hover:text-red-700"
                      title="Löschen"
                    >
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-[var(--text-3)] max-w-2xl">
          <ShieldCheck size={14} className="text-[#D4AF37] mt-0.5 flex-shrink-0" />
          <span>
            Passwörter werden mit bcrypt gehashed. Benutzer können sich danach unter
            der Login-Seite mit ihrer E-Mail und dem gesetzten Passwort einloggen.
          </span>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="card w-full max-w-xl p-6 fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">{editId ? "Benutzer bearbeiten" : "Neuer Benutzer"}</h2>
              <button onClick={() => setOpen(false)} className="text-[var(--text-3)] hover:text-[var(--text)]"><X size={18} /></button>
            </div>
            {error && (
              <div data-testid="user-form-error" className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm mb-4">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">Name *</label>
                <input required data-testid="user-form-name" className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">E-Mail *</label>
                <input
                  required
                  type="email"
                  data-testid="user-form-email"
                  className={`input-base ${editId ? "opacity-60 cursor-not-allowed" : ""}`}
                  value={form.email}
                  readOnly={!!editId}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
                {editId && (
                  <p className="text-[11px] text-[var(--text-3)] mt-1.5">E-Mail kann nach Erstellung nicht mehr geändert werden.</p>
                )}
              </div>
              <div className="col-span-2">
                <label className="label-eyebrow block mb-2">{editId ? "Neues Passwort (leer lassen = unverändert)" : "Passwort *"}</label>
                <input
                  required={!editId}
                  type="text"
                  minLength={editId ? 0 : 6}
                  data-testid="user-form-password"
                  className="input-base font-mono"
                  placeholder={editId ? "leer lassen = unverändert" : "min. 6 Zeichen"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Rolle *</label>
                <select required data-testid="user-form-role" className="input-base" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  <option value="customer">Kunde</option>
                  <option value="dispatcher">Disponent</option>
                  <option value="admin">Administrator</option>
                  <option value="driver">Fahrer</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Telefon</label>
                <input data-testid="user-form-phone" className="input-base" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              {form.role === "customer" && (
                <div className="col-span-2">
                  <label className="label-eyebrow block mb-2">Verknüpfter Kunde *</label>
                  <select required data-testid="user-form-customer" className="input-base" value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                    <option value="">— Bitte wählen —</option>
                    {customers.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}{c.city ? ` · ${c.city}` : ""}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[var(--text-3)] mt-1.5">
                    Dieser Benutzer sieht ausschliesslich Aufträge des verknüpften Kunden.
                  </p>
                </div>
              )}
              {form.role === "driver" && (
                <div className="col-span-2">
                  <label className="label-eyebrow block mb-2">Fahrer-Profil verknüpfen (Sub-Konto)</label>
                  <select
                    data-testid="user-form-driver"
                    className="input-base"
                    value={form.driver_id || ""}
                    onChange={(e) => setForm({ ...form, driver_id: e.target.value })}
                  >
                    <option value="">— Eigenes Fahrer-Profil (kein Sub-Konto) —</option>
                    {drivers.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}{d.email ? ` · ${d.email}` : ""}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-[var(--text-3)] mt-1.5">
                    Für Sub-Fahrer (z.B. Angestellte von Sky/Stähli): Wähle hier den Haupt-Fahrer.
                    Der Sub-Fahrer sieht dann dieselben Touren wie der Haupt-Fahrer und kann Status setzen.
                  </p>
                </div>
              )}
              {form.role === "driver" && (
                <div className="col-span-2">
                  <label className="flex items-center gap-3 cursor-pointer p-3 border border-[var(--border)] rounded-sm hover:border-[var(--border-strong)]">
                    <input
                      type="checkbox"
                      data-testid="user-form-hide-payout"
                      checked={!!form.hide_payout}
                      onChange={(e) => setForm({ ...form, hide_payout: e.target.checked })}
                      className="accent-[#D4AF37] w-4 h-4"
                    />
                    <div>
                      <div className="text-sm text-[var(--text)] font-medium">Vergütung verbergen (Sub-Account)</div>
                      <div className="text-[11px] text-[var(--text-3)] mt-0.5">
                        Aktiviere dies für Fahrer-Sub-Accounts (z.B. Angestellte von Stähli/Sky), die zwar Touren sehen, aber NICHT den vereinbarten Pauschalbetrag.
                      </div>
                    </div>
                  </label>
                </div>
              )}
              <div className="col-span-2 flex justify-end gap-2 mt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-sm rounded-sm uppercase tracking-wider">Abbrechen</button>
                <button type="submit" data-testid="user-form-submit" className="btn-gold px-4 py-2 text-sm rounded-sm uppercase tracking-wider">{editId ? "Speichern" : "Anlegen"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
